import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Kedarkantha = () => {
  const tourData = {
    name: "Kedarkantha Trek",
    duration: "04 Nights 05 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935181/aavzpyu1xwmalssimnfa.webp",
    altitude: "12,500 ft",
    difficulty: "Easy-Moderate",
    region: "Uttarakhand",
    highlights: ["Winter trek", "Summit views", "Forest trails"],
    bestTime: "December to April",
    description: "The Kedarkantha Trek is one of the most popular winter treks in India, offering stunning 360-degree views of the snow-capped Himalayan peaks. This trek is perfect for beginners and experienced trekkers alike."
  };

  return (
    <>
      <Helmet>
        <title>Kedarkantha Trek - NomadTrailz</title>
        <meta name="description" content="Experience the magical Kedarkantha Trek in Uttarakhand. Perfect winter trek with stunning 360-degree Himalayan views." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img src={tourData.image} alt={tourData.name} />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A perfect winter trek with 360-degree Himalayan views
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div className="info-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>This trek takes you through dense pine forests, beautiful meadows, and snow-covered landscapes, culminating in breathtaking panoramic views from the summit.</p>
                </motion.div>

                <motion.div className="highlights-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
                  <h3>Highlights</h3>
                  <ul>
                    <li>360-degree Himalayan views</li>
                    <li>Perfect winter trekking experience</li>
                    <li>Snow-covered landscapes</li>
                    <li>Dense pine forests</li>
                    <li>Beautiful meadows</li>
                    <li>Beginner-friendly trek</li>
                  </ul>
                </motion.div>

                <motion.div className="itinerary-card" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Dehradun to Sankri</h4>
                    <ul>
                      <li>Pickup from Dehradun in the morning to travel 240 km (10-hour) long scenic distance to Sankri through Mussoorie, Barkot, Purola, Mori, and Netwar along the Tons River.</li>
                      <li>The experience throughout the journey is simply breathtaking because the route passes through green meadows and scenic valleys.</li>
                      <li>Located in the Uttarkashi district, on the slopes of the Himalayas, lies the peaceful village of Sankri.</li>
                      <li>Sankri isn't just a picturesque village, but also the gateway to numerous famous treks, including the Kedarkantha Trek, Har Ki Dun, Rupin Pass, Bali Pass, and so many others.</li>
                      <li>At an altitude of 6,400 feet above ground, the town comes under the jurisdiction of the Govind Wildlife Sanctuary.</li>
                      <li>After that, you stay in Sankri and enjoy a great dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Trek from Sankri to Juda-ka-talab</h4>
                    <ul>
                      <li>After our morning workout, we will eat breakfast and then go out for our first camp at Juda Ka Talab at 8 AM.</li>
                      <li>Today, you'll trek a total of 5 kilometres.</li>
                      <li>During this journey, one will witness several shepherd houses in wide-open meadows along this walk, all of which are beautiful to view.</li>
                      <li>In a lovely pine forest, approximately ten minutes' walk from the settlement, the route begins.</li>
                      <li>The route passes through dense pine and oak forests, offering stunning views of the surrounding mountains.</li>
                      <li>Camp at Juda Ka Talab for the night, enjoying the peaceful mountain environment.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Juda-ka-talab to Kedarkantha Base Camp</h4>
                    <ul>
                      <li>Today involves trekking from Juda Ka Talab to Kedarkantha Base Camp.</li>
                      <li>The trail becomes more challenging with steep ascents as you gain elevation.</li>
                      <li>You'll encounter snow-covered patches and alpine meadows along the way.</li>
                      <li>The views become more spectacular as you climb higher into the mountains.</li>
                      <li>Camp at Kedarkantha Base Camp, experiencing the high-altitude environment.</li>
                      <li>Prepare for the summit attempt the next day.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Kedarkantha Base Camp to Summit and Back to Juda-ka-talab</h4>
                    <ul>
                      <li>The most exciting day of the trek! Early morning start for the summit attempt.</li>
                      <li>You'll reach the Kedarkantha Peak at 12,500 feet, offering breathtaking panoramic views.</li>
                      <li>The snow-cloaked mountains cast their shadows on the ground during sunrise, and it is an astonishing sight to behold.</li>
                      <li>This is one of the most rewarding and satisfying moments of the trek.</li>
                      <li>After spending time at the summit, descend back to Juda Ka Talab for the night.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Juda-ka-talab to Sankri and Drive to Dehradun</h4>
                    <ul>
                      <li>The final day involves descending back to Sankri, completing the trek.</li>
                      <li>Enjoy the descent through the same scenic routes you climbed.</li>
                      <li>Arrive back in Sankri and board the vehicle for the return journey to Dehradun.</li>
                      <li>Travel back through the same scenic route, reminiscing about the incredible adventure.</li>
                      <li>Arrive in Dehradun with memories of an unforgettable Himalayan trek.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Transportation support on Traveller, Ertiga, or Innova (depending on the size of the group) throughout the journey</li>
                    <li>Accommodation on sharing basis</li>
                    <li>All meals (Veg)</li>
                    <li>A well-experienced and certified trek leader</li>
                    <li>Cook</li>
                    <li>Supporting staff</li>
                    <li>Forest permit</li>
                    <li>Camping equipment (tents, sleeping bags, mattresses)</li>
                    <li>Kitchen and dining tent with all necessary utensils</li>
                    <li>Porters for carrying common equipment</li>
                    <li>Safety equipment and communication devices</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                    <li>Porters or mules to carry your luggage. The cost per bag (10/12 kg) is 300 per day</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Dehradun</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div className="booking-card" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>12,500 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>20 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy-Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>December to April</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Dehradun)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Dehradun:</strong> Dehradun railway station between 06:00 AM to 07:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>360-degree Himalayan views</li>
                    <li>Perfect winter trekking experience</li>
                    <li>Snow-covered landscapes</li>
                    <li>Dense pine forests</li>
                    <li>Beautiful meadows</li>
                    <li>Beginner-friendly trek</li>
                    <li>Breathtaking sunrise views</li>
                    <li>Professional trek leader and support staff</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Kedarkantha;
